import { useEffect, useRef } from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { cn } from '@/lib/utils';

import { DateRangeCalendar } from './DateRangeCalendar';
import { type DateRangeLayoutProps } from './DateRangeMenu';
import { DateRangePresetList } from './DateRangePresetList';
import { useDateRangeDraft } from './useDateRangeDraft';

export const DateRangePopover = ({
  startDate,
  endDate,
  selectedPeriod,
  onPresetSelect,
  onRangeSelect,
  isPresetDisabled,
  isDayDisabled,
  retentionStart,
  today,
  align = 'start',
  onRequestClose,
  footer,
}: DateRangeLayoutProps & { onRequestClose?: () => void }) => {
  const isNarrow = useBreakpoint('md');
  const { shown, isDrafting, handleSelect } = useDateRangeDraft(startDate, endDate, onRangeSelect);

  const requestClose = useRef(onRequestClose);
  requestClose.current = onRequestClose;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        requestClose.current?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      role="dialog"
      className={cn(
        'absolute top-full left-0 z-30 mt-1 inline-flex items-start gap-3 overflow-hidden rounded-md border border-border bg-background shadow-md',
        align === 'end' && 'right-0 left-auto'
      )}
    >
      <div className="flex min-w-40 flex-col items-start gap-1 self-stretch border-r border-border px-3 py-4">
        <DateRangePresetList
          selected={isDrafting ? null : selectedPeriod}
          onSelect={onPresetSelect}
          isDisabled={isPresetDisabled}
        />
        {footer}
      </div>
      <DateRangeCalendar
        selected={shown}
        onSelect={handleSelect}
        isDayDisabled={isDayDisabled}
        startMonth={retentionStart ?? undefined}
        endMonth={today}
        numberOfMonths={isNarrow ? 1 : 2}
      />
    </div>
  );
};
